import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, TextField, Alert, CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import useStore from '../../config/store';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REASONS = ['email_in_use', 'not_same_person', 'other_rut', 'email_not_confirmed', 'pending_email', 'file_email_in_use'];

/**
 * Motivo de una postulación "Por validar" (PLAN_IMPORTACION_COMPUTRABAJO,
 * fase 0), para la tarjeta, el drawer y el diálogo.
 */
export const validationReasonText = (t, validation) => {
  const v = validation || {};
  let key = 'generic';
  if (REASONS.includes(v.reason)) key = v.reason;
  else if (REASONS.includes(v.type)) key = v.type;
  return t(`ats.validation.reason.${key}`, {
    email: v.pendingEmail || '—',
    name: v.conflictApplicantName || '—',
  });
};

/** Teléfono canónico para mostrar ('56912345678' → '+56 9 1234 5678') */
export const formatValidationPhone = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 11 && digits.startsWith('569')) {
    return `+56 9 ${digits.slice(3, 7)} ${digits.slice(7)}`;
  }
  if (digits.length === 11 && digits.startsWith('51')) {
    return `+51 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }
  return digits.length > 9 ? `+${digits}` : String(phone);
};

/**
 * "Validar" una postulación "Por validar".
 *  - Correo en uso: se asigna a la ficha el correo pendiente o el que escriba
 *    el reclutador (si sigue en otra ficha, el backend avisa cuál).
 *  - Correo no confirmado (portal): el reclutador confirma que es la persona
 *    y su cuenta del portal se vincula a la ficha. Si la ficha no tiene
 *    correo y el de la cuenta ya está en otra ficha, el backend lo avisa y
 *    se pide otro correo para la ficha.
 * onValidated(application, patch) refleja el cambio en el kanban.
 */
const AtsValidateDialog = ({ open, application, onClose, onValidated }) => {
  const { t } = useTranslation();
  const validation = application?.validation || {};
  // Correo no confirmado: el correo se pide solo si el backend lo necesita
  const [askEmail, setAskEmail] = useState(false);
  const needsEmail = validation.type !== 'email_not_confirmed' || askEmail;
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setEmail(validation.pendingEmail || '');
      setAskEmail(false);
      setError(null);
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!application) return null;

  const handleValidate = async () => {
    const appId = application.uid || application.id;
    const typed = email.trim().toLowerCase();
    if (needsEmail && !EMAIL_RE.test(typed)) {
      setError(t('ats.validation.invalidEmail'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { authToken, showAlert } = useStore.getState();
      const { data } = await api.patch(
        `/applications/${appId}/validate`,
        needsEmail ? { email: typed } : {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const result = data.response?.data || {};
      const notLinked = result.linked === false;
      if (result.status === 'blocked') {
        // El correo con que quedó está en la lista negra de la organización
        showAlert(t('ats.validation.blocked'), 'warning');
      } else {
        showAlert(notLinked ? t('ats.validation.notLinked') : t('ats.validation.success'), notLinked ? 'warning' : 'success');
      }
      onValidated(application, {
        status: result.status || 'active',
        candidateEmail: result.candidateEmail !== undefined ? result.candidateEmail : application.candidateEmail,
        validation: result.validation || validation,
      });
    } catch (err) {
      const resp = err.response?.data?.response;
      if (resp?.errorType === 'EMAIL_IN_USE') {
        setError(t('ats.validation.emailInUse', { email: resp.email || typed, name: resp.applicant?.name || '—' }));
        if (!needsEmail) {
          // Correo no confirmado en una ficha sin correo: el de la cuenta ya
          // está en otra ficha → se escribe otro para la ficha
          setAskEmail(true);
          setEmail('');
        }
      } else if (resp?.errorType === 'INVALID_EMAIL') {
        setError(t('ats.validation.invalidEmail'));
        if (!needsEmail) setAskEmail(true);
      } else {
        setError(t('ats.validation.error'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('ats.validation.dialogTitle', { name: application.candidateName || '—' })}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {validationReasonText(t, validation)}
        </Typography>
        {validation.contactPhone && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ats.validation.phone', { phone: formatValidationPhone(validation.contactPhone) })}
          </Typography>
        )}
        {validation.type === 'email_not_confirmed' && (
          <Typography variant="body2" sx={{ mb: needsEmail ? 2 : 0 }}>{t('ats.validation.confirmPerson')}</Typography>
        )}
        {needsEmail && (
          <TextField
            fullWidth
            size="small"
            label={t('ats.validation.emailLabel')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            helperText={t('ats.validation.emailHelp')}
            disabled={saving}
            inputProps={{ 'data-testid': 'validate-email' }}
          />
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          {t('ats.validation.cancel')}
        </Button>
        <Button
          onClick={handleValidate}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
        >
          {t('ats.validation.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AtsValidateDialog;
